import {UnitDto} from './unit-model';
import {CommodityType} from '../_zen-legacy-common/_models/commodity';
import {ElecBillDto, ElecBillCreateRequest, ElecBillUpdateRequest, ElecBillRequest} from './bills-v4-elec.model';
import {GasBillDto, GasBillCreateRequest, GasBillUpdateRequest, GasBillRequest} from './bills-v4-gas.model';

export * from './bills-v4-elec.model';
export * from './bills-v4-gas.model';

export interface BillIdentificationInformation {
  meterId: number;
  utilityAccountNum1: string;
  utilityAccountNum2: string;
  utilityAccountNum3: string;
  energyPlanId: number;
  supplierId: string; // UUID as string
  utilityId: number;
  unitId: number;
  rateClassId: number;
  commodityType: CommodityType;
}

export interface BillGeneralInformation {
  billType: BillType;
  billUploadDate: string;  // ZonedDateTime as string
  billDate: string;  // LocalDate as string
  billDueDate: string;  // LocalDate as string
  billStartDate: string;  // LocalDate as string
  billEndDate: string;  // LocalDate as string
  periodBillTotal: number;
}

/**
 * Detail line item inside a charge bucket. Mirrors the backend `LineItem` POJO
 * (com.zentility.api.entity.bill.LineItem) which is stored in the JSON columns
 * `general_line_items` / `usage_line_items` / `supply_line_items` /
 * `distribution_line_items` on `bill_elec` / `bill_gas`.
 *
 * Backend field mapping (LineItem → BillLineItem):
 *   String name        → name   (human description)
 *   BigDecimal cost    → cost
 *   Integer usage      → usage
 *   String unit        → unit
 *   BigDecimal rate    → rate
 *   JsonNode LineItemData → lineItemData
 *
 * Bucket encoding: each JSON column is a FLAT array per bill section (supply,
 * distribution, …) with no per-sub-bucket key. To round-trip line items back to
 * the correct {@link import('../../../ai-onboarding/models').ChargeGroup} (e.g.
 * `supplyEnergyCharges` vs `supplyCapacityCharges`), the originating bucket key is
 * carried in `lineItemData.category`. The `name` stays the clean human description.
 */
export interface BillLineItem {
  /** Human-readable charge description (← ChargeLineItem.description). */
  name?: string;
  /** Charge amount in dollars (← ChargeLineItem.amount). */
  cost?: number;
  /** Usage quantity for this line (← ChargeLineItem.usage). Backend column is an integer. */
  usage?: number;
  /** Usage unit for this line, e.g. "kWh" (← ChargeLineItem.unit). */
  unit?: string;
  /** Per-unit rate (← ChargeLineItem.rate). */
  rate?: number;
  /**
   * Free-form passthrough (backend JsonNode `LineItemData`). Carries `category` —
   * the ParsedBillData ChargeGroup field key this item belongs to (e.g.
   * 'supplyEnergyCharges') — so {@link import('../../../ai-onboarding/services/bill-persistence.service').BillPersistenceService}
   * can regroup the flat array back into per-bucket ChargeGroups on load.
   */
  lineItemData?: { category?: string; [k: string]: unknown };
}

export interface BillAdditionalInformation {
  previousBalance: number;
  meterReadingPrev: number;
  meterReading: number;
  meterMultiplier: number;
  meterNextReadDate: string; // LocalDate as string
  supplierPaymentId: string; // UUID as a string
  physicalUtilityFile: boolean;
  physicalSupplierFile: boolean;
}

export interface BaseBillDto {
  id: number;
  identificationInformation?: BillIdentificationInformation;
  generalInformation?: BillGeneralInformation;
  additionalInformation?: BillAdditionalInformation;
  unitDto: UnitDto;
  totalUsagePlatform: number;
  createDt: string;
  createdByUserId: string;
  updateDt: string;

  // --- Charge-bucket line items (JSON columns on bill_elec / bill_gas) ---
  // Optional on read: these populate only once the backend response DTO surfaces
  // the entity's generalLineItems / usageLineItems / supplyLineItems /
  // distributionLineItems columns. See {@link BillLineItem} for bucket encoding.
  generalLineItems?: BillLineItem[];
  usageLineItems?: BillLineItem[];
  supplyLineItems?: BillLineItem[];
  distributionLineItems?: BillLineItem[];
}

export interface BaseBillRequest {
  // Identification
  meterId: number;
  utilityAccountNum1: string;
  utilityAccountNum2: string;
  utilityAccountNum3: string;
  energyPlanId: number;
  supplierId: string;
  utilityId: number;
  unitId: number;
  unitDto: UnitDto;
  rateClassId: number;

  // General
  billType: BillType;
  billUploadDate: Date;
  billDate: Date;
  billDueDate: Date;
  billStartDate: Date;
  billEndDate: Date;
  periodBillTotal: number;

  // Additional
  previousBalance: number;
  meterReadingPrev: number;
  meterReading: number;
  meterMultiplier: number;
  meterNextReadDate: Date;
  supplierPaymentId: string;
  physicalUtilityFile: boolean;
  physicalSupplierFile: boolean;

  // Charge-bucket line items (JSON columns). Optional on write — sent in budgeting
  // mode so detail line items round-trip. The backend create/PATCH DTO accepts these;
  // Spring Boot ignores them harmlessly until that DTO change deploys.
  generalLineItems?: BillLineItem[];
  usageLineItems?: BillLineItem[];
  supplyLineItems?: BillLineItem[];
  distributionLineItems?: BillLineItem[];

  // AI request log id of the extraction the bill was read from; null on an update keeps the current link
  billAiId?: number;

  // Other (not in DB)
  tempFileId: string;
  supplierTempFileId: string;
}

export type BillDto = ElecBillDto | GasBillDto;
export type BillRequest = ElecBillRequest | GasBillRequest;
export type BillCreateRequest = ElecBillCreateRequest | GasBillCreateRequest;
export type BillUpdateRequest = ElecBillUpdateRequest | GasBillUpdateRequest;

// Per-commodity lookup types: pass a narrowed CommodityType literal (or a generic C extends
// CommodityType) and get the concrete elec/gas shape back. Lets callers avoid Partial<BillRequest>
// (which silently accepts mixed-commodity field literals) when commodity is known.
export type BillDtoFor<C extends CommodityType> =
  C extends CommodityType.Electricity ? ElecBillDto :
  C extends CommodityType.Gas ? GasBillDto :
  never;

export type BillRequestFor<C extends CommodityType> =
  C extends CommodityType.Electricity ? ElecBillRequest :
  C extends CommodityType.Gas ? GasBillRequest :
  never;

export type BillCreateRequestFor<C extends CommodityType> =
  C extends CommodityType.Electricity ? ElecBillCreateRequest :
  C extends CommodityType.Gas ? GasBillCreateRequest :
  never;

export type BillUpdateRequestFor<C extends CommodityType> =
  C extends CommodityType.Electricity ? ElecBillUpdateRequest :
  C extends CommodityType.Gas ? GasBillUpdateRequest :
  never;

export function isElecBill(b: BillDto): b is ElecBillDto {
  return b?.commodityType === CommodityType.Electricity;
}

export function isGasBill(b: BillDto): b is GasBillDto {
  return b?.commodityType === CommodityType.Gas;
}

// Alias to the union; keep until all consumers migrate to BillDto / ElecBillDto / GasBillDto explicitly.
export type BillsV4ListContentModel = BillDto;

export interface BillOverlapResponse {
  errorPrefix: string;
  overlapList: BillOverlapResponseChild[];
}

export interface BillOverlapResponseChild {
  id: number;
  billStartDate: Date;
  billEndDate: Date;
}

export interface BillUploadedDocument {
  created: Date;
  customerId: number;
  description: string;
  documentId: number;
  documentType: string;
  downloadUrl: string;
  energyPlanId: number;
  hidden: boolean;
  lastModified: Date;
  mimeType: string;
  name: string;
  organizationId: string;
  originalFileName: string;
  serviceAddressId: number;
  signatureApplied: boolean;
  signatures: string[];
  signed: boolean;
  size: number;
  userId: string;
}

export enum BillType {
  // Consolidated Billing - both Utility and Supplier Bills
  CONSOLIDATED = 'CONSOLIDATED',
  // Dual Billing - separate Supplier and Utility Bills
  DUAL = 'DUAL',
}

export enum PhysicalBillType {
  UTILITY = 'UTILITY',
  SUPPLIER = 'SUPPLIER'
}
